/**
 * BUD-18: `nhash`, the immutable reference to a hashtree root.
 *
 * bech32 with the human-readable part `nhash` over a TLV payload:
 *
 *   type 0 (required): 32 byte root manifest hash
 *   type 5 (optional): 32 byte root client-side key
 *
 * Type 5 is deliberately local to `nhash` so the optional root key can never be
 * confused with NIP-19's relay / author / kind fields.
 */

import { bech32 } from "@scure/base";
import { concatBytes, fromHex, toHex } from "./bytes";

export const NHASH_PREFIX = "nhash";
export const TLV_ROOT_HASH = 0;
export const TLV_ROOT_KEY = 5;

/** bech32 length cap. NIP-19 style identifiers use a generous limit. */
const BECH32_LIMIT = 1500;

export interface NhashParts {
  rootHash: Uint8Array;
  /** Present when the root manifest is encrypted. A bearer secret. */
  rootKey?: Uint8Array;
}

function tlvRecord(type: number, value: Uint8Array): Uint8Array {
  if (value.length > 255) throw new Error("TLV values are limited to 255 bytes");
  return concatBytes(new Uint8Array([type, value.length]), value);
}

/** Build the raw TLV payload. Exposed so the UI can show the bytes before bech32. */
export function encodeNhashPayload({ rootHash, rootKey }: NhashParts): Uint8Array {
  if (rootHash.length !== 32) throw new Error("root hash must be 32 bytes");
  if (rootKey && rootKey.length !== 32) throw new Error("root key must be 32 bytes");

  const records = [tlvRecord(TLV_ROOT_HASH, rootHash)];
  if (rootKey) records.push(tlvRecord(TLV_ROOT_KEY, rootKey));
  return concatBytes(...records);
}

export function encodeNhash(parts: NhashParts): string {
  const payload = encodeNhashPayload(parts);
  return bech32.encode(NHASH_PREFIX, bech32.toWords(payload), BECH32_LIMIT);
}

export function decodeNhash(nhash: string): NhashParts {
  const { prefix, words } = bech32.decode(nhash as `${string}1${string}`, BECH32_LIMIT);
  if (prefix !== NHASH_PREFIX) throw new Error(`expected an ${NHASH_PREFIX} identifier, got ${prefix}`);

  const payload = bech32.fromWords(words);

  // Legacy form: a bare 32 byte payload is the root hash with no key.
  if (payload.length === 32) return { rootHash: new Uint8Array(payload) };

  let offset = 0;
  let rootHash: Uint8Array | undefined;
  let rootKey: Uint8Array | undefined;

  while (offset < payload.length) {
    const type = payload[offset];
    const length = payload[offset + 1];
    if (type === undefined || length === undefined) throw new Error("truncated TLV record");
    const value = new Uint8Array(payload.slice(offset + 2, offset + 2 + length));
    if (value.length !== length) throw new Error("truncated TLV value");

    if (type === TLV_ROOT_HASH) rootHash = value;
    else if (type === TLV_ROOT_KEY) rootKey = value;
    // Other types are reserved for future BUD-18 extensions and are ignored.

    offset += 2 + length;
  }

  if (!rootHash || rootHash.length !== 32) throw new Error("nhash is missing a valid root hash");
  if (rootKey && rootKey.length !== 32) throw new Error("nhash has an invalid root key");

  return { rootHash, ...(rootKey ? { rootKey } : {}) };
}

/** `htree://<nhash>[/<path>]` — the immutable reference form. */
export function immutableHtreeUri(parts: NhashParts, path: string[] = []): string {
  const segments = path.map(encodeURIComponent);
  return `htree://${encodeNhash(parts)}${segments.length ? `/${segments.join("/")}` : ""}`;
}

/**
 * `htree://<npub>/<tree-name>[/<path>]` — the mutable reference form.
 *
 * Each segment is percent-encoded independently. A tree name is one logical
 * segment, so a `/` inside it must be encoded rather than splitting the path.
 */
export function mutableHtreeUri(npub: string, treeName: string, path: string[] = []): string {
  const segments = [encodeURIComponent(treeName), ...path.map(encodeURIComponent)];
  return `htree://${npub}/${segments.join("/")}`;
}

/** Convenience for the UI, which holds hashes as hex strings. */
export function encodeNhashHex(rootHashHex: string, rootKeyHex?: string): string {
  return encodeNhash({
    rootHash: fromHex(rootHashHex),
    ...(rootKeyHex ? { rootKey: fromHex(rootKeyHex) } : {}),
  });
}

export { toHex as nhashToHex };
